import { useCombinationArticle } from "@coworker/app/src/components/RoomSettings/hooks/useCombinationArticle";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";
import FullScreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import { ArticleError } from "@coworker/apprestructured/src/shared/components/ArticleError/ArticleError";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import { ProductQualityError } from "@coworker/apprestructured/src/shared/components/ProductQualityError/productQualityError";
import { SalesLocationsAndStock } from "@coworker/apprestructured/src/shared/components/SalesLocationsAndStock/SalesLocationsAndStock";
import { ItemError } from "@coworker/apprestructured/src/shared/enums/itemError";
import { useItemExpectedDelivery } from "@coworker/apprestructured/src/shared/hooks/item/useItemExpectedDelivery";
import { usePIPHome } from "@coworker/apprestructured/src/shared/hooks/item/usePIPHome";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState.js";
import FixaCarousel from "@coworker/apprestructured/src/shared/wrappers/FixaCarousel/FixaCarousel";
import FixaImage from "@coworker/apprestructured/src/shared/wrappers/FixaImage/FixaImage";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import { TaskTypeNames } from "@coworker/apprestructured/src/task-management/constants/TaskTypeNames.js";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes.js";
import { useRelatedTasksForProduct } from "@coworker/apprestructured/src/tasks/hooks/useRelatedTasksForProduct";
import featureNames from "@coworker/enums/featureNames";
import { Trans, useTranslation } from "@coworker/locales";
import { formatProductIdWithDots } from "@coworker/reusable/formatters/product.formatter";
import { isRDTDevice } from "@coworker/reusable/helpers/devices";
import { generateLivligProductLink } from "@coworker/reusable/helpers/livligIntegration";
import { PositionedLoaderIcon } from "@coworker/reusable/Loader";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import styled from "styled-components";
import { useInputPopup } from "../../components/InputPopup";
import { CopyToClipboardButton } from "../../components/RoomSettings/Atoms/CopyToClipboardButton";
import { useCountryId, useStoreId } from "../../core/auth/useLoggedInUser";
import {
  default as tracker,
  default as trackerHelper,
} from "../../helpers/tracker";
import { useAllSalesLocationsForArticleQuery } from "../../hooks/Locations/useAllSalesLocationsForArticleQuery";
import { useAvailableElevatedStock } from "../../hooks/useAvailableElevatedStock"; // TODO: Change to use from tasks-service only!
import { useProductQualityTeamId } from "../../hooks/useProductQualityTeamId";
import { parseProduct } from "../../services/products.service";
import {
  DistancedRow,
  InformationWrapper,
  NumberBadge,
  NumberWrapper,
  ProductIdAndPriceWrapper,
  ProductInfo,
  ProductShowcase,
  Subtitle,
  Title,
} from "./ProductShowcase";

const ImageWrapper = styled("div")`
  margin: 16px 24px 6px 24px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CarouselImage = styled(FixaImage)`
  height: 250px;
  object-fit: contain;
`;

const RelatedTasks = styled(FixaListViewItem)`
  span {
    color: var(--new);
  }
`;

const ProductImagesCarousel = ({ images, getFallback }) => (
  <ImageWrapper>
    <FixaCarousel id="pip-images">
      {images.map((image, index) => (
        <CarouselImage
          key={`image${index}`}
          src={image ?? ""}
          fallback={getFallback(image)}
        />
      ))}
    </FixaCarousel>
  </ImageWrapper>
);

const RelatedTasksListItem = ({ count, onClick }) => (
  <RelatedTasks
    title={
      <Trans count={count}>
        {count === 1
          ? "relatedTasksCountString"
          : "relatedTasksCountString_plural"}
      </Trans>
    }
    inset
    control="navigational"
    controlIcon="chevronRight"
    emphasised
    onClick={onClick}
  />
);

const MediaMaintenanceListItem = ({ productId, navigate }) => (
  <FixaListViewItem
    id="media-maintenance-pip-link"
    title={<Trans>mediaMaintenancePIPLink</Trans>}
    inset
    control="navigational"
    controlIcon="chevronRight"
    emphasised
    onClick={() => navigate(`/mediamaintenance/articles/search/${productId}`)}
  />
);

const PIPCommonActionListItems = ({
  onAction,
  isPriceOrCurrencyMissing,
  includeProductQuality,
  productQualityTitleKey,
}) => {
  const { t, i18n } = useTranslation();

  const sortedTaskTypeItems = useMemo(() => {
    const locale = i18n?.resolvedLanguage || i18n?.language;
    const taskTypeItems = [
      {
        key: "addon-order",
        titleKey: "addonOrderString",
        onClick: () => onAction("addon", isPriceOrCurrencyMissing),
      },
      {
        key: "product-issue",
        titleKey: "productString",
        onClick: () => onAction("product"),
      },
      {
        key: "create-task",
        titleKey: "createTaskLinkString",
        onClick: () => onAction("create_task"),
      },
      includeProductQuality
        ? {
            key: "product-quality",
            titleKey: productQualityTitleKey,
            onClick: () => onAction("product_quality"),
          }
        : null,
    ].filter(Boolean);

    return taskTypeItems.sort((a, b) =>
      t(a.titleKey).localeCompare(t(b.titleKey), locale, {
        sensitivity: "base",
        numeric: true,
      })
    );
  }, [
    i18n?.language,
    i18n?.resolvedLanguage,
    includeProductQuality,
    isPriceOrCurrencyMissing,
    onAction,
    productQualityTitleKey,
    t,
  ]);

  return sortedTaskTypeItems.map((taskTypeItem) => (
    <FixaListViewItem
      key={taskTypeItem.key}
      id={taskTypeItem.key}
      title={<Trans>{taskTypeItem.titleKey}</Trans>}
      inset
      control="navigational"
      controlIcon="chevronRight"
      emphasised
      onClick={taskTypeItem.onClick}
    />
  ));
};

let livligLinkClicked = false;

const PIPHomeViewInner = ({
  productData,
  short_id,
  type,
  isBreathTakingItem,
  onAction,
  isPriceOrCurrencyMissing,
}) => {
  const full_id = type + short_id;
  const {
    available,
    elevated,
    color,
    loading: isStockLoading,
  } = useAvailableElevatedStock(full_id);
  const store_id = useStoreId();
  const isRDT = isRDTDevice();
  const { data: relatedTasks } = useRelatedTasksForProduct(short_id);
  const navigate = useFixaNavigate();

  const { data: itemDeliveryData, isLoading: isDeliveryDataLoading } =
    useItemExpectedDelivery(short_id, store_id);
  const trackLinkDisplaySentIfLivligLinkHasNotBeenClicked = (
    _store_id,
    _short_id
  ) => {
    if (!livligLinkClicked) {
      tracker.livlig.trackLinkDisplay(_store_id, _short_id, featureNames.PIP);
    }
    livligLinkClicked = false;
  };

  useEffect(() => {
    return () => {
      trackLinkDisplaySentIfLivligLinkHasNotBeenClicked(store_id, short_id);
    };
  }, [store_id, short_id]);

  return (
    <>
      <ProductShowcase
        name={productData?.name}
        price={productData?.salesPrice?.price}
        currency={productData?.salesPrice?.currency}
        priceUnit={productData?.salesPrice?.unit}
        originalPrice={productData?.salesPrice?.previousPrice?.price}
        isBreathTakingItem={isBreathTakingItem}
        isFamilyPrice={productData?.salesPrice?.isFamilyPrice}
        newLowerPrice={productData?.salesPrice?.newLowerPrice}
        description={productData.description || ""}
        formattedNumber={formatProductIdWithDots(productData.no)}
        elevated={elevated}
        color={color}
        available={available}
        isStockLoading={isStockLoading}
        itemDeliveryData={itemDeliveryData}
        isDeliveryDataLoading={isDeliveryDataLoading}
      />
      <FixaListView id={productData.no}>
        {relatedTasks?.list?.length > 0 && (
          <RelatedTasksListItem
            count={relatedTasks.list.length}
            onClick={() => {
              tracker.trackGlobalActionClick("related_tasks");
              navigate(`/tasks/related/${productData.no}`);
            }}
          />
        )}
        <PIPCommonActionListItems
          onAction={onAction}
          isPriceOrCurrencyMissing={isPriceOrCurrencyMissing}
          includeProductQuality
          productQualityTitleKey="capitalCaseProductQualityReportString"
        />
        <SalesLocationsAndStock itemNoAndType={{ no: short_id, type }} />
        {!isRDT && (
          <FixaListViewItem
            id="test"
            title={<Trans>livligLinkWidgetTitle</Trans>}
            inset
            control="navigational"
            emphasised
            onClick={() => {
              tracker.trackGlobalActionClick("livlig_link");
              livligLinkClicked = true;
              tracker.livlig.trackLinkClick(
                store_id,
                short_id,
                featureNames.PIP
              );
              window.open(
                generateLivligProductLink(store_id, short_id),
                "_blank"
              );
            }}
          />
        )}
        <MediaMaintenanceListItem productId={short_id} navigate={navigate} />
      </FixaListView>
    </>
  );
};

export function PIPHomeView() {
  const { full_id: maybeFullId } = useParams();
  const navigate = useFixaNavigate();
  const [shortId, type] = parseProduct(maybeFullId);
  const storeId = useStoreId();
  const userCountryId = useCountryId();
  const productQualityTeamId = useProductQualityTeamId();
  const { closePopup } = useInputPopup();

  const { data: pipHomeData, isLoading } = usePIPHome(shortId);
  const { data: relatedTasks } = useRelatedTasksForProduct(shortId);

  const isPriceOrCurrencyMissing =
    !pipHomeData?.salesPrice?.price || !pipHomeData?.salesPrice?.currency;

  const { data: combinationArticle } = useCombinationArticle(
    storeId,
    userCountryId,
    shortId,
    { enabled: type === "SCI" }
  );

  const { isLoading: isLoadingLocations } = useAllSalesLocationsForArticleQuery(
    type && shortId ? `${type}${shortId}` : ""
  );

  const [showArticleErrorModal, setShowArticleErrorModal] = useState(false);
  const [showProductQualityErrorModal, setShowProductQualityErrorModal] =
    useState(false);
  const toggleArticleErrorModal = () =>
    setShowArticleErrorModal((prev) => !prev);

  const { data: taskTypes, isLoading: isLoadingTaskTypes } = useAllTaskTypes();
  const [, setSearchParams] = useSearchParamState();
  const enableAddonOrdersV2 = useFlag(FLAGS.ENABLE_ADDON_ORDERS_V2);

  const isLoadingData = isLoading || isLoadingLocations || isLoadingTaskTypes;

  function getTaskTypeId(taskTypeName) {
    return taskTypes?.find((type) => type.name === taskTypeName)?.id;
  }

  const onAction = (which, isPriceMissing = false) => {
    if (!which) return;

    if (which === "addon" && isPriceMissing) {
      setShowArticleErrorModal(true);
      return;
    }

    closePopup();
    tracker.trackGlobalActionClick(which);
    const productIssueId = getTaskTypeId(TaskTypeNames.PRODUCT_ISSUE);
    const addonOrderId = getTaskTypeId(TaskTypeNames.ADDON_ORDER);
    const customTaskId = getTaskTypeId(TaskTypeNames.CUSTOM_TASK);
    const productQualityId = getTaskTypeId(TaskTypeNames.PRODUCT_QUALITY);

    if (which === "product_quality") {
      if (!productQualityTeamId) {
        setShowProductQualityErrorModal(true);
        return;
      }
      if (productQualityId) {
        setSearchParams((prev) => {
          prev.set("taskCreator", String(productQualityId));
          prev.set(
            "initialValues",
            JSON.stringify({
              details: {
                item: {
                  no: shortId,
                  type,
                },
              },
            })
          );
        });
      }
      return;
    }

    if (enableAddonOrdersV2 && addonOrderId && which === "addon") {
      setSearchParams((prev) => {
        prev.set("taskCreator", String(addonOrderId));
        prev.set(
          "initialValues",
          JSON.stringify({
            details: {
              item: {
                no: shortId,
                type: type,
              },
            },
          })
        );
      });
    } else if (productIssueId && which === "product") {
      setSearchParams((prev) => {
        prev.set("taskCreator", String(productIssueId));
        prev.set(
          "initialValues",
          JSON.stringify({
            details: {
              item: {
                no: shortId,
                type: type,
              },
            },
          })
        );
      });
    } else if (which === "create_task") {
      setSearchParams((prev) => {
        prev.set("taskCreator", String(customTaskId));
        prev.set(
          "initialValues",
          JSON.stringify({
            details: {
              items: [
                {
                  no: shortId,
                  type: type,
                },
              ],
            },
          })
        );
      });
    } else {
      // This last step, if all else fails, we just navigate to the old v1 task creation
      navigate(`/task/new/${which}`, {
        state: {
          product: { supplierNumber: "", product: shortId, type },
          qualityTeamId: productQualityTeamId,
        },
      });
    }
  };

  useEffect(() => {
    trackerHelper.trackPIPEntered();
  }, []);

  const { images, bigImage } = pipHomeData?.productImages || {};
  const chosenImages = useMemo(
    () => (images?.length ? images : [bigImage]),
    [images, bigImage]
  );

  const productData = useMemo(
    () =>
      !isLoadingData && pipHomeData
        ? {
            name: pipHomeData.name,
            description: pipHomeData.description,
            measurements: pipHomeData.measurements,
            no: pipHomeData.no,
            shortId,
            salesPrice: pipHomeData.salesPrice,
            isFamilyPrice: pipHomeData?.salesPrice?.isFamilyPrice,
            newLowerPrice: pipHomeData?.salesPrice?.newLowerPrice,
            location: pipHomeData.location || "?",
            locationDescription: pipHomeData.locationDescription || "?",
          }
        : {},
    [isLoadingData, pipHomeData, shortId]
  );

  if (type === "SCI") {
    const hasCombination = combinationArticle?.length > 0;
    const combinationData = hasCombination ? combinationArticle[0] : null;

    return (
      <FullScreenPopup
        noPadding
        testId="pip-home-view"
        linkbar={
          <Linkbar
            currentLabel={combinationData?.name.split(" ")[0] || ""}
            loading={hasCombination ? !combinationData?.name : true}
          />
        }
      >
        <ProductImagesCarousel
          images={chosenImages}
          getFallback={(image) => !image}
        />
        <ProductInfo>
          <ProductIdAndPriceWrapper>
            <NumberWrapper>
              <NumberBadge>{combinationData?.noFormatted || ""}</NumberBadge>
              <CopyToClipboardButton
                valueForClipboard={shortId.replace(/\./g, "")}
                onClick={() => {}}
              />
            </NumberWrapper>
          </ProductIdAndPriceWrapper>
          <DistancedRow>
            <InformationWrapper>
              <div>
                <Title>{combinationData?.name.split(" ")[0]}</Title>
                <Subtitle>
                  {combinationData
                    ? combinationData.name
                        .split(" ")
                        .slice(2)
                        .filter((part) => part !== "0")
                        .join(" ")
                    : ""}
                </Subtitle>
              </div>
            </InformationWrapper>
          </DistancedRow>
        </ProductInfo>
        <FixaListView id={productData.no}>
          {relatedTasks?.list?.length > 0 && (
            <RelatedTasksListItem
              count={relatedTasks.list.length}
              onClick={() => navigate(`/tasks/related/${combinationData?.no}`)}
            />
          )}
          <PIPCommonActionListItems
            onAction={onAction}
            isPriceOrCurrencyMissing={isPriceOrCurrencyMissing}
            includeProductQuality={!!productQualityTeamId}
            productQualityTitleKey="productQualityReportString"
          />
          <MediaMaintenanceListItem productId={shortId} navigate={navigate} />
        </FixaListView>
      </FullScreenPopup>
    );
  }

  return (
    <FullScreenPopup
      noPadding
      testId="pip-home-view"
      linkbar={
        <Linkbar currentLabel={productData.name} loading={!productData.name} />
      }
    >
      {isLoadingData && <PositionedLoaderIcon />}
      {!isLoadingData && (
        <>
          <ProductImagesCarousel
            images={chosenImages}
            getFallback={(image) => image === ""}
          />
          <PIPHomeViewInner
            productData={productData}
            short_id={productData.shortId}
            type={type}
            isBreathTakingItem={pipHomeData?.isBreathTakingItem ?? ""}
            onAction={onAction}
            isPriceOrCurrencyMissing={isPriceOrCurrencyMissing}
          />
        </>
      )}
      <ArticleError
        itemErrorType={ItemError.ITEM_PRICE_MISSING}
        openArticleErrorModal={showArticleErrorModal}
        onClose={toggleArticleErrorModal}
      />
      <ProductQualityError
        openProductQualityErrorModal={showProductQualityErrorModal}
        onClose={setShowProductQualityErrorModal}
      />
    </FullScreenPopup>
  );
}
